import React, { useEffect, useState, forwardRef } from "react";
import axios from "axios";
import {
  Card,
  Grid,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Box,
  TextField,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Avatar,
  Divider,
  Grow,
  Zoom,
  Fade,
  Paper,
} from "@mui/material";
import { keyframes } from "@mui/system";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BASE_URL from "BASE_URL";

// Icons
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Premium Animations
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-50px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulseRing = keyframes`
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
`;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`;

const glow = keyframes`
  0% { box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4); }
  50% { box-shadow: 0 8px 30px rgba(239, 68, 68, 0.8); }
  100% { box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4); }
`;

const bounceIn = keyframes`
  0% { opacity: 0; transform: scale(0.3); }
  50% { opacity: 1; transform: scale(1.05); }
  70% { transform: scale(0.9); }
  100% { transform: scale(1); }
`;

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

function NotificationManagement() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Add/Edit Dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "admin",
  });
  const [submitting, setSubmitting] = useState(false);

  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const token = localStorage.getItem("token");

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/notification/display-notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setNotifications(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch notifications", "error");
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      showSnackbar(error.response?.data?.message || "Error fetching notifications", "error");
    } finally {
      setLoading(false);
    }
  };

  // Refresh data
  const refreshData = async () => {
    setRefreshing(true);
    await fetchNotifications();
    setRefreshing(false);
    showSnackbar("Notifications refreshed!", "success");
  };

  // Add notification
  const handleAddNotification = async () => {
    if (!formData.title.trim()) {
      showSnackbar("Please enter title", "warning");
      return;
    }
    if (!formData.message.trim()) {
      showSnackbar("Please enter message", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/notification/create-notification`,
        {
          title: formData.title,
          message: formData.message,
          type: formData.type,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        showSnackbar("Notification created successfully!", "success");
        setOpenDialog(false);
        resetForm();
        fetchNotifications();
      } else {
        showSnackbar(response.data.message || "Failed to create notification", "error");
      }
    } catch (error) {
      console.error("Error creating notification:", error);
      showSnackbar(error.response?.data?.message || "Error creating notification", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete notification
  const handleDeleteNotification = async () => {
    if (!selectedNotification) return;

    setDeleting(true);
    try {
      const response = await axios.delete(
        `${BASE_URL}/notification/delete-notification?id=${selectedNotification._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        showSnackbar("Notification deleted successfully!", "success");
        setDeleteDialogOpen(false);
        setSelectedNotification(null);
        fetchNotifications();
      } else {
        showSnackbar(response.data.message || "Failed to delete notification", "error");
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
      showSnackbar(error.response?.data?.message || "Error deleting notification", "error");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const resetForm = () => {
    setFormData({
      title: "",
      message: "",
      type: "admin",
    });
  };

  const openDeleteDialog = (notification) => {
    setSelectedNotification(notification);
    setDeleteDialogOpen(true);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);

    // Format date: 23 Apr 2026
    const formattedDate = date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    // Format time: 11:12 AM (Indian timezone)
    const formattedTime = date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });

    return `${formattedDate} | ${formattedTime}`;
  };

  // Get notification type icon and color
  const getTypeConfig = (type) => {
    switch (type) {
      case "admin":
        return { icon: <AdminPanelSettingsIcon />, color: "#1565C0", bgColor: "#e3f2fd" };
      case "info":
        return { icon: <InfoIcon />, color: "#00838f", bgColor: "#e0f7fa" };
      case "warning":
        return { icon: <WarningIcon />, color: "#e65100", bgColor: "#fff3e0" };
      case "success":
        return { icon: <CheckCircleIcon />, color: "#2e7d32", bgColor: "#e8f5e9" };
      default:
        return { icon: <NotificationsActiveIcon />, color: "#6a1b9a", bgColor: "#f3e5f5" };
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3} sx={{ animation: `${fadeInUp} 0.5s ease-out` }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "24px" }}>
              {/* Header Section */}
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <NotificationsActiveIcon sx={{ color: "#fff", fontSize: 28 }} />
                  <MDTypography variant="h6" color="white">
                    Notification Management
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1}>
                  {/* <Tooltip title="Refresh Notifications">
                    <MDButton
                      variant="gradient"
                      color="dark"
                      startIcon={<RefreshIcon />}
                      onClick={refreshData}
                      disabled={refreshing}
                      sx={{ borderRadius: "12px", textTransform: "none" }}
                    >
                      {refreshing ? "Refreshing..." : "Refresh"}
                    </MDButton>
                  </Tooltip> */}
                  <Tooltip title="Add Notification">
                    <MDButton
                      variant="gradient"
                      color="dark"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenDialog(true)}
                      sx={{ borderRadius: "12px", textTransform: "none" }}
                    >
                      Add Notification
                    </MDButton>
                  </Tooltip>
                </MDBox>
              </MDBox>

              {/* Notifications Content */}
              <MDBox p={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : notifications.length === 0 ? (
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    py={8}
                    sx={{ animation: `${pulse} 2s infinite` }}
                  >
                    <NotificationsActiveIcon sx={{ fontSize: 80, color: "#bdbdbd", mb: 2 }} />
                    <MDTypography variant="h6" color="text" fontWeight="medium">
                      No Notifications Yet
                    </MDTypography>
                    <MDTypography variant="body2" color="text" sx={{ mt: 1, mb: 3 }}>
                      Click the &quot;Add Notification&quot; button to create your first
                      notification
                    </MDTypography>
                    <MDButton
                      variant="gradient"
                      color="info"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenDialog(true)}
                    >
                      Create First Notification
                    </MDButton>
                  </MDBox>
                ) : (
                  <Grid container spacing={2}>
                    {notifications.map((notification, index) => {
                      const typeConfig = getTypeConfig(notification.type);
                      return (
                        <Grid item xs={12} key={notification._id}>
                          <Box
                            sx={{
                              animation: `${slideIn} 0.3s ease-out ${index * 0.05}s backwards`,
                              transition: "all 0.3s ease",
                              "&:hover": {
                                transform: "translateX(5px)",
                              },
                            }}
                          >
                            <Card
                              sx={{
                                position: "relative",
                                borderRadius: "16px",
                                overflow: "hidden",
                                border: "1px solid #e0e0e0",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  boxShadow: 3,
                                  borderColor: typeConfig.color,
                                },
                              }}
                            >
                              {/* Colored left border */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: "4px",
                                  bgcolor: typeConfig.color,
                                }}
                              />

                              <MDBox p={2}>
                                <Grid container spacing={2} alignItems="flex-start">
                                  {/* Avatar Section */}
                                  <Grid item xs="auto">
                                    <Avatar
                                      sx={{
                                        bgcolor: typeConfig.bgColor,
                                        color: typeConfig.color,
                                        width: 48,
                                        height: 48,
                                      }}
                                    >
                                      {typeConfig.icon}
                                    </Avatar>
                                  </Grid>

                                  {/* Content Section */}
                                  <Grid item xs>
                                    <MDBox
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="flex-start"
                                    >
                                      <MDBox flex={1}>
                                        <MDBox display="flex" alignItems="center" gap={1} mb={0}>
                                          <MDTypography variant="h6" fontWeight="bold">
                                            {notification.title}
                                          </MDTypography>
                                          <Chip
                                            label={notification.type.toUpperCase()}
                                            size="small"
                                            sx={{
                                              bgcolor: typeConfig.bgColor,
                                              color: typeConfig.color,
                                              fontWeight: "bold",
                                              fontSize: "10px",
                                              height: "20px",
                                            }}
                                          />
                                          {/* {!notification.isRead && (
                                            <Chip
                                              label="NEW"
                                              size="small"
                                              sx={{
                                                bgcolor: "#ff4444",
                                                color: "#fff",
                                                fontWeight: "bold",
                                                fontSize: "10px",
                                                height: "20px",
                                                animation: `${pulse} 1.5s infinite`,
                                              }}
                                            />
                                          )} */}
                                        </MDBox>
                                        <MDTypography
                                          variant="button"
                                          color="text"
                                          fontWeight="regular"
                                          sx={{ mb: 1 }}
                                        >
                                          {notification.message}
                                        </MDTypography>
                                        <MDTypography
                                          variant="caption"
                                          color="text"
                                          fontWeight="medium"
                                          display="block"
                                        >
                                          {formatDate(notification.createdAt)}
                                        </MDTypography>
                                      </MDBox>

                                      {/* Actions Section */}
                                      <MDBox display="flex" gap={1}>
                                        <Tooltip title="Delete Notification">
                                          <IconButton
                                            size="small"
                                            onClick={() => openDeleteDialog(notification)}
                                            sx={{
                                              color: "#f44336",
                                              "&:hover": { bgcolor: "#ffebee" },
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Tooltip>
                                      </MDBox>
                                    </MDBox>
                                  </Grid>
                                </Grid>
                              </MDBox>
                            </Card>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Add Notification Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => !submitting && setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "20px" },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold">
              Create New Notification
            </MDTypography>
            <IconButton onClick={() => setOpenDialog(false)} disabled={submitting}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox py={1}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter notification title..."
              variant="outlined"
              margin="normal"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
            <TextField
              fullWidth
              label="Message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Enter notification message..."
              variant="outlined"
              margin="normal"
              multiline
              rows={4}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
            <TextField
              fullWidth
              select
              label="Type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              variant="outlined"
              margin="normal"
              SelectProps={{ native: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            >
              <option value="admin">Admin</option>
            </TextField>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            disabled={submitting}
            sx={{ borderRadius: "10px", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddNotification}
            disabled={submitting}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              bgcolor: "#4caf50",
              color: "#fff",
              "&:hover": { bgcolor: "#45a049" },
            }}
          >
            {submitting ? <CircularProgress size={24} /> : "Create Notification"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "32px",
            background: "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !deleting && setDeleteDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(239, 68, 68, 0.1)",
            },
          }}
          disabled={deleting}
        >
          <CloseIcon sx={{ color: "#ef4444" }} />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0) 70%)",
            pointerEvents: "none",
          }}
        />

        <DialogTitle sx={{ pt: 4, pb: 2, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Grow in={deleteDialogOpen} timeout={600}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Zoom in={deleteDialogOpen} timeout={600}>
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    borderRadius: "50%",
                    animation: `${pulseRing} 2s infinite`,
                    cursor: "pointer",
                    mb: 2,
                    "&:hover": { animation: `${shakeAnimation} 0.5s ease-in-out` },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(239, 68, 68, 0.4)",
                      animation: `${glow} 2s ease-in-out infinite`,
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 40, color: "white" }} />
                  </Box>
                </Box>
              </Zoom>

              <Box sx={{ animation: `${bounceIn} 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)` }}>
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold">
                    Delete Notification?
                  </MDTypography>
                </Box>
              </Box>
            </Box>
          </Grow>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={deleteDialogOpen} timeout={800}>
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "100ms" }}>
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{
                    borderRadius: "16px",
                    mb: 3,
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                  }}
                >
                  <MDTypography variant="body2" fontWeight="medium" color="error">
                    This action cannot be undone!
                  </MDTypography>
                </Alert>
              </Zoom>

              <Grow in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "150ms" }}>
                <Paper
                  elevation={0}
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(239,68,68,0.05) 0%, rgba(239,68,68,0.02) 100%)",
                    borderRadius: "20px",
                    p: 3,
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.02)",
                      borderColor: "rgba(239, 68, 68, 0.4)",
                    },
                  }}
                >
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    fontWeight="bold"
                  >
                    NOTIFICATION TO BE DELETED
                  </MDTypography>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#ef4444", wordBreak: "break-word" }}
                  >
                    &ldquo;{selectedNotification?.title}&ldquo;
                  </MDTypography>
                  {/* <MDTypography
                    variant="body2"
                    color="text"
                    sx={{ mt: 1, wordBreak: "break-word" }}
                  >
                    {selectedNotification?.message}
                  </MDTypography> */}
                </Paper>
              </Grow>

              <Fade in={deleteDialogOpen} timeout={800} style={{ transitionDelay: "200ms" }}>
                <Box sx={{ mt: 3 }}>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="medium"
                    display="block"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                    This notification will be permanently removed from the system
                  </MDTypography>
                </Box>
              </Fade>
            </Box>
          </Fade>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 4,
            pt: 2,
            gap: 2,
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "250ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                color: "#111",
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
            >
              Cancel
            </MDButton>
          </Zoom>

          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "300ms" }}>
            <MDButton
              variant="contained"
              color="error"
              onClick={handleDeleteNotification}
              disabled={deleting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            >
              {deleting ? "DELETING..." : "DELETE PERMANENTLY"}
            </MDButton>
          </Zoom>
        </DialogActions>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
            animation: `${shimmer} 2s infinite linear`,
            backgroundSize: "200% 100%",
          }}
        />
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Slide}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default NotificationManagement;
